import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useDebounce } from '../hooks/useDebounce';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { Pagination } from '../components/Pagination';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { ShieldAlert, Search, Filter, Phone, Mail, ChevronDown, ChevronRight } from 'lucide-react';

interface DNCRecord {
  channel: 'email' | 'sms' | 'phone' | string;
  revoked_at: string | null;
}

type VerificationSummary = {
  value: string | null;
  status: string;
  provider?: string | null;
  checked_at: string;
};

type VerificationByCustomerId = Record<
  string,
  {
    email: VerificationSummary | null;
    phone: VerificationSummary | null;
  }
>;

type PhoneIntelligence = {
  phone_normalized: string;
  phone_e164: string | null;
  is_valid: boolean | null;
  activity_score: number | null;
  line_type: string | null;
  carrier: string | null;
  is_prepaid: boolean | null;
  is_litigator_risk: boolean | null;
  checked_at: string | null;
};

type PhoneIntelByPhone = Record<string, PhoneIntelligence | null>;

interface Customer {
  id: string;
  service_account_id: string | null;
  company_id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone_number: string | null;
  address1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  agreement_status: string | null;
  last_service_date: string | null;
  last_service_amount: number | null;
  customer_do_not_contact?: DNCRecord[];
}

type BillingLookup = {
  billing_customer_id: string | null;
  label: string | null;
  error?: string | null;
};

type BillingLookupByKey = Record<string, BillingLookup>;

type PhoneHierarchyLocation = {
  location_id: string;
  legacy_location_id: string | null;
  service_account_id: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  internal_memberships_count: number;
  has_internal_membership: boolean;
};

type PhoneHierarchyBillingAccount = {
  billing_customer_id: string | null;
  label: string | null;
  locations: PhoneHierarchyLocation[];
};

type LocationDrilldownJob = {
  job_id: string | null;
  job_number: string | null;
  status: string | null;
  job_class: string | null;
  job_type?: string | null;
  description?: string | null;
  department?: string | null;
  lead_source?: string | null;
  priority?: string | null;
  total_invoice_amount?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  scheduled_for: string | null;
  completed_date: string | null;
  fetched_at: string | null;
};

type JobsPagination = {
  page: number;
  size: number;
  total_jobs: number | null;
  total_pages: number | null;
  has_more: boolean;
};

type LocationDrilldownAgreement = {
  agreement_id: string;
  agreement_number: string | null;
  agreement_type_name: string | null;
  status: any;
  terms: any;
  detail: any;
};

type LocationDrilldownResponse = {
  jobs_source: string;
  jobs_pagination?: JobsPagination;
  jobs: LocationDrilldownJob[];
  agreements: LocationDrilldownAgreement[];
  internal_memberships: InternalMembershipSummary[];
  counts?: any;
};

type LocationDrilldownState = {
  loading: boolean;
  error: string | null;
  data: LocationDrilldownResponse | null;
};

type CustomerJob = {
  job_id: string;
  job_number: string | null;
  status: string | null;
  job_class: string | null;
  scheduled_for: string | null;
  completed_date: string | null;
  fetched_at: string;
  location_id?: string | null;
  service_account_id?: string | null;
  total_invoice_amount?: number | null;
  job_type?: string | null;
  description?: string | null;
};

type SuccesswareMembershipStatus = {
  is_member: boolean;
  membership_type: string | null;
  membership_code: string | null;
  member_since: string | null;
  benefits: any;
  checked_at: string;
};

type SuccesswareMembershipEvent = {
  id: string;
  event_type: string;
  detected_at: string;
};

type InternalMembershipSummary = {
  id: string;
  membership_number: string;
  status: string;
  plan_name: string;
  start_date: string | null;
  next_billing_date: string | null;
  cancelled_at: string | null;
  created_at: string;
};

type EnrichedLocation = {
  id?: string;
  company_id?: string;
  location_id: string;
  phone_normalized?: string;
  full_address?: string | null;
  street?: string | null;
  unit?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  property_type?: string | null;
  total_jobs_at_location?: number | null;
  total_revenue_at_location?: number | null;
  first_service_at_location?: string | null;
  last_service_at_location?: string | null;
};

type EnrichedAgreement = {
  id?: string;
  company_id?: string;
  agreement_id: string;
  agreement_type?: string | null;
  status?: string | null;
  start_date?: string | null;
  renewal_date?: string | null;
  expiration_date?: string | null;
  monthly_amount?: number | null;
  annual_amount?: number | null;
};

type CompanyOption = {
  id: string;
  label: string;
};

const COMPANY_OPTIONS: CompanyOption[] = [
  { id: '0f88534f-2ba8-412b-a554-c55ef5fdcf0e', label: 'Hales AC' },
  { id: '1212061e-f319-406e-b5c6-ebcb4c46e9d6', label: 'Climate Control (MyCCS)' },
  { id: '2ce92bf5-062b-4e8d-9ba3-4efc45a31921', label: 'Abraham AC' },
];

const COMPANY_MAP = Object.fromEntries(COMPANY_OPTIONS.map((c) => [c.id, c.label]));

const AGREEMENT_OPTIONS = ['Active', 'Expired', 'Terminated', 'Never'];

const PAGE_SIZE = 25;
const RAW_PAGE_MULTIPLIER = 4;

function normalizePhoneKey(phone: string | null | undefined): string | null {
  const digits = String(phone ?? '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  if (digits.length > 10) return digits.slice(-10);
  return digits.length >= 10 ? digits : null;
}

function isNumericId(id: string) {
  return /^\d+$/.test(id);
}

function canonicalizeCustomerId(id: string | null | undefined) {
  const raw = String(id ?? '').trim();
  if (!raw) return null;

  // IDs may be stored as "company:12345" (older imports) or "12345".
  // Canonicalize to the trailing component so prefixed/non-prefixed rows dedupe cleanly.
  if (raw.includes(':')) {
    const parts = raw.split(':');
    return parts[parts.length - 1] || raw;
  }

  return raw;
}

function formatCustomerDisplayName(
  c: Pick<Customer, 'first_name' | 'last_name' | 'company_name'>
) {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  return c.company_name || 'Unknown';
}

function formatDateShort(value: string | null | undefined) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString();
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function formatMoney(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `$${Number(value).toFixed(2)}`;
}

function scoreCustomerForDedupe(c: Customer) {
  const id = String(c.id || '');
  let score = 0;

  // Prefer canonical numeric SuccessWare location IDs.
  if (isNumericId(id)) score += 100;
  // Slightly de-prioritize prefixed IDs (company:12345).
  if (id.includes(':')) score -= 5;

  if (c.phone_number) score += 5;
  if (c.email) score += 3;
  if (c.first_name) score += 2;
  if (c.last_name) score += 2;
  if (c.address1) score += 2;
  if (c.city) score += 1;
  if (c.state) score += 1;
  if (c.zip) score += 1;
  if (c.last_service_date) score += 1;
  if (c.agreement_status) score += 1;

  return score;
}

function dedupeCustomersForList(rows: Customer[]) {
  // Group by company + canonical SuccessWare locationId so:
  // - prefixed/non-prefixed duplicates collapse ("climate:123" vs "123")
  // - *distinct* accounts that share a phone number remain selectable
  const groups = new Map<
    string,
    {
      best: Customer;
      bestScore: number;
      bestIndex: number;
      count: number;
    }
  >();

  rows.forEach((c, index) => {
    const canonicalId = canonicalizeCustomerId(c.id);
    const key = `${c.company_id}|${canonicalId || c.id}`;

    const score = scoreCustomerForDedupe(c);
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { best: c, bestScore: score, bestIndex: index, count: 1 });
      return;
    }

    existing.count += 1;
    if (score > existing.bestScore) {
      existing.best = c;
      existing.bestScore = score;
      existing.bestIndex = index;
    }
  });

  const deduped = Array.from(groups.values())
    .sort((a, b) => a.bestIndex - b.bestIndex)
    .map((g) => g.best);

  const hidden = Math.max(0, rows.length - deduped.length);
  return { deduped, hidden };
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [rawRowCount, setRawRowCount] = useState<number>(0);
  const [hiddenDuplicateCount, setHiddenDuplicateCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const [verificationsByCustomerId, setVerificationsByCustomerId] =
    useState<VerificationByCustomerId>({});
  const [verificationsLoading, setVerificationsLoading] = useState(false);
  const verificationsReqIdRef = useRef(0);

  const [phoneIntelByPhone, setPhoneIntelByPhone] = useState<PhoneIntelByPhone>({});
  const [phoneIntelLoading, setPhoneIntelLoading] = useState(false);
  const phoneIntelReqIdRef = useRef(0);

  const [billingLookupByKey, setBillingLookupByKey] = useState<BillingLookupByKey>({});
  const [billingLookupLoading, setBillingLookupLoading] = useState(false);
  const [billingLookupError, setBillingLookupError] = useState<string | null>(null);
  const billingLookupReqIdRef = useRef(0);

  const [phoneHierarchyBillingAccounts, setPhoneHierarchyBillingAccounts] = useState<
    PhoneHierarchyBillingAccount[]
  >([]);
  const [phoneHierarchyLoading, setPhoneHierarchyLoading] = useState(false);
  const [phoneHierarchyError, setPhoneHierarchyError] = useState<string | null>(null);
  const phoneHierarchyReqIdRef = useRef(0);

  const [expandedBillingAccountKeys, setExpandedBillingAccountKeys] = useState<Record<string, boolean>>({});
  const [expandedLocationIds, setExpandedLocationIds] = useState<Record<string, boolean>>({});
  const [locationDrilldownByLocationId, setLocationDrilldownByLocationId] = useState<
    Record<string, LocationDrilldownState>
  >({});

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [agreementFilter, setAgreementFilter] = useState<string[]>([]);
  const [dncOnly, setDncOnly] = useState(false);
  const [lastServiceOnly, setLastServiceOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [isDncModalOpen, setIsDncModalOpen] = useState(false);
  const [dncTarget, setDncTarget] = useState<Customer | null>(null);
  const [dncChannel, setDncChannel] = useState<'email' | 'sms' | 'phone'>('email');
  const [dncReason, setDncReason] = useState('');
  const [dncSubmitting, setDncSubmitting] = useState(false);
  const [dncError, setDncError] = useState<string | null>(null);

  // Customer 360 (on-demand)
  const [isCustomer360Open, setIsCustomer360Open] = useState(false);
  const [customer360Target, setCustomer360Target] = useState<Customer | null>(null);
  const [customer360Loading, setCustomer360Loading] = useState(false);
  const [customer360Syncing, setCustomer360Syncing] = useState(false);
  const [customer360Error, setCustomer360Error] = useState<string | null>(null);
  const [customer360Jobs, setCustomer360Jobs] = useState<CustomerJob[]>([]);
  const [customer360Membership, setCustomer360Membership] =
    useState<SuccesswareMembershipStatus | null>(null);
  const [customer360MembershipEvents, setCustomer360MembershipEvents] = useState<
    SuccesswareMembershipEvent[]
  >([]);
  const [customer360InternalMemberships, setCustomer360InternalMemberships] = useState<
    InternalMembershipSummary[]
  >([]);
  const [customer360Locations, setCustomer360Locations] = useState<EnrichedLocation[]>([]);
  const [customer360Agreements, setCustomer360Agreements] = useState<EnrichedAgreement[]>([]);

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedCompanies, agreementFilter, dncOnly, lastServiceOnly, page]);

  const fetchVerificationsFor = async (customerIds: string[]) => {
    const reqId = ++verificationsReqIdRef.current;

    try {
      setVerificationsLoading(true);

      if (customerIds.length === 0) {
        setVerificationsByCustomerId({});
        return;
      }

      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();

      if (sessionErr) throw sessionErr;
      const token = session?.access_token;
      if (!token) {
        // Not logged in; don’t show verification status.
        setVerificationsByCustomerId({});
        return;
      }

      const resp = await fetch('/api/verifications/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ customer_ids: customerIds }),
      });

      let payload: any = null;
      try {
        payload = await resp.json();
      } catch {
        // ignore
      }

      if (!resp.ok) {
        throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
      }

      if (reqId !== verificationsReqIdRef.current) return;
      setVerificationsByCustomerId((payload?.data || {}) as VerificationByCustomerId);
    } catch (err) {
      console.error('Failed to load verifications', err?.message || err);
      if (reqId === verificationsReqIdRef.current) {
        setVerificationsByCustomerId({});
      }
    } finally {
      if (reqId === verificationsReqIdRef.current) {
        setVerificationsLoading(false);
      }
    }
  };

  const fetchPhoneIntelFor = async (phones: (string | null | undefined)[]) => {
    const reqId = ++phoneIntelReqIdRef.current;

    try {
      setPhoneIntelLoading(true);

      const normalizedPhones = phones
        .map(normalizePhoneKey)
        .filter((p): p is string => !!p);

      const uniquePhones = [...new Set(normalizedPhones)];

      if (uniquePhones.length === 0) {
        setPhoneIntelByPhone({});
        return;
      }

      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();

      if (sessionErr) throw sessionErr;
      const token = session?.access_token;
      if (!token) {
        setPhoneIntelByPhone({});
        return;
      }

      const resp = await fetch(`/api/phone-intel/bulk?phones=${uniquePhones.join(',')}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let payload: any = null;
      try {
        payload = await resp.json();
      } catch {
        // ignore
      }

      if (!resp.ok) {
        throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
      }

      if (reqId !== phoneIntelReqIdRef.current) return;
      setPhoneIntelByPhone((payload?.data || {}) as PhoneIntelByPhone);
    } catch (err: any) {
      console.error('Failed to load phone intelligence', err?.message || err);
      if (reqId === phoneIntelReqIdRef.current) {
        setPhoneIntelByPhone({});
      }
    } finally {
      if (reqId === phoneIntelReqIdRef.current) {
        setPhoneIntelLoading(false);
      }
    }
  };

  const fetchBillingLookupFor = async ({
    phoneDigits,
    companyIds,
    enabled,
  }: {
    phoneDigits: string | null;
    companyIds: string[];
    enabled: boolean;
  }) => {
    const reqId = ++billingLookupReqIdRef.current;

    try {
      setBillingLookupLoading(true);
      setBillingLookupError(null);

      if (!enabled || !phoneDigits) {
        setBillingLookupByKey({});
        return;
      }

      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();

      if (sessionErr) throw sessionErr;
      const token = session?.access_token;
      if (!token) {
        setBillingLookupByKey({});
        return;
      }

      const resp = await fetch('/api/admin/customers/billing-hierarchy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone: phoneDigits,
          company_ids: Array.isArray(companyIds) ? companyIds : [],
        }),
      });

      let payload: any = null;
      try {
        payload = await resp.json();
      } catch {
        // ignore
      }

      if (!resp.ok) {
        throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
      }

      if (reqId !== billingLookupReqIdRef.current) return;

      const mapping = (payload?.mapping || {}) as BillingLookupByKey;
      setBillingLookupByKey(mapping);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Failed to load billing lookup', msg);
      if (reqId === billingLookupReqIdRef.current) {
        setBillingLookupByKey({});
        setBillingLookupError(msg);
      }
    } finally {
      if (reqId === billingLookupReqIdRef.current) {
        setBillingLookupLoading(false);
      }
    }
  };

  const fetchPhoneHierarchyFor = async ({
    phoneDigits,
    companyId,
    enabled,
  }: {
    phoneDigits: string | null;
    companyId: string | null;
    enabled: boolean;
  }) => {
    const reqId = ++phoneHierarchyReqIdRef.current;

    try {
      setPhoneHierarchyLoading(true);
      setPhoneHierarchyError(null);

      if (!enabled || !phoneDigits || !companyId) {
        setPhoneHierarchyBillingAccounts([]);
        setExpandedBillingAccountKeys({});
        setExpandedLocationIds({});
        setLocationDrilldownByLocationId({});
        return;
      }

      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();

      if (sessionErr) throw sessionErr;
      const token = session?.access_token;
      if (!token) {
        setPhoneHierarchyBillingAccounts([]);
        return;
      }

      const resp = await fetch('/api/admin/customers/phone-hierarchy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone: phoneDigits,
          company_id: companyId,
        }),
      });

      let payload: any = null;
      try {
        payload = await resp.json();
      } catch {
        // ignore
      }

      if (!resp.ok) {
        throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
      }

      if (reqId !== phoneHierarchyReqIdRef.current) return;

      const rawAccounts = (payload?.billing_accounts || []) as PhoneHierarchyBillingAccount[];
      const sortedAccounts = [...rawAccounts].sort((a, b) => {
        const aUnresolved = !a?.billing_customer_id;
        const bUnresolved = !b?.billing_customer_id;
        if (aUnresolved !== bUnresolved) return aUnresolved ? 1 : -1;

        const idCmp = String(a.billing_customer_id || '').localeCompare(
          String(b.billing_customer_id || ''),
          undefined,
          { numeric: true, sensitivity: 'base' }
        );
        if (idCmp) return idCmp;

        return String(a.label || '').localeCompare(String(b.label || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });

      setPhoneHierarchyBillingAccounts(sortedAccounts);
      setExpandedBillingAccountKeys({});
      setExpandedLocationIds({});
      setLocationDrilldownByLocationId({});
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Failed to load phone hierarchy', msg);
      if (reqId === phoneHierarchyReqIdRef.current) {
        setPhoneHierarchyBillingAccounts([]);
        setPhoneHierarchyError(msg);
      }
    } finally {
      if (reqId === phoneHierarchyReqIdRef.current) {
        setPhoneHierarchyLoading(false);
      }
    }
  };

  const fetchLocationDrilldownFor = async ({
    phoneDigits,
    companyId,
    locationId,
    page = 1,
    size = 10,
    append = false,
  }: {
    phoneDigits: string;
    companyId: string;
    locationId: string;
    page?: number;
    size?: number;
    append?: boolean;
  }) => {
    setLocationDrilldownByLocationId((prev) => ({
      ...prev,
      [locationId]: {
        loading: true,
        error: null,
        data: prev?.[locationId]?.data || null,
      },
    }));

    try {
      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();

      if (sessionErr) throw sessionErr;
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const resp = await fetch('/api/admin/customers/location-drilldown', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone: phoneDigits,
          company_id: companyId,
          location_id: locationId,
          page,
          size,
          include_agreements: page === 1,
        }),
      });

      let payload: any = null;
      try {
        payload = await resp.json();
      } catch {
        // ignore
      }

      if (!resp.ok) {
        throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
      }

      setLocationDrilldownByLocationId((prev) => {
        const nextData = payload as LocationDrilldownResponse;
        const existing = prev?.[locationId]?.data;

        if (append && existing) {
          const mergedJobs = [...(existing.jobs || []), ...(nextData.jobs || [])];
          const seen = new Set<string>();
          const dedupedJobs = mergedJobs.filter((j) => {
            const key = j.job_id || j.job_number;
            if (!key) return true;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

          return {
            ...prev,
            [locationId]: {
              loading: false,
              error: null,
              data: {
                ...existing,
                jobs: dedupedJobs,
                jobs_source: nextData.jobs_source || existing.jobs_source,
                jobs_pagination: nextData.jobs_pagination || existing.jobs_pagination,
              },
            },
          };
        }

        return {
          ...prev,
          [locationId]: {
            loading: false,
            error: null,
            data: nextData,
          },
        };
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Failed to load location drilldown', msg);

      setLocationDrilldownByLocationId((prev) => ({
        ...prev,
        [locationId]: {
          loading: false,
          error: msg,
          data: prev?.[locationId]?.data || null,
        },
      }));
    }
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const from = (page - 1) * PAGE_SIZE;
      const term = debouncedSearch.trim();
      const rawMultiplier = term ? 1 : RAW_PAGE_MULTIPLIER;
      const to = from + PAGE_SIZE * rawMultiplier - 1;

      let query = supabase
        .from('sw_customers')
        .select(
          `id, service_account_id, company_id, first_name, last_name, company_name, email, phone_number, address1, city, state, zip, agreement_status, last_service_date, last_service_amount, customer_do_not_contact(channel, revoked_at)`,
          { count: 'exact' }
        )
        .eq('is_active', true)
        .order('added_at', { ascending: false })
        .range(from, to);

      if (term) {
        const digitsRaw = term.replace(/\D/g, '');
        let digits = digitsRaw;
        if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
        if (digits.length > 10) digits = digits.slice(-10);

        const isPhoneLikeSearch = digits.length >= 7;

        // Phone-like searches (>=7 digits) should not search address fields; it's slow and very noisy.
        // Keep it tight to phone + id for speed/relevance.
        if (isPhoneLikeSearch) {
          const likeDigits = `%${digits}%`;
          query = query.or([`phone_number.ilike.${likeDigits}`, `id.ilike.${likeDigits}`].join(','));
        } else {
          const like = `%${term}%`;
          query = query.or(
            [
              `first_name.ilike.${like}`,
              `last_name.ilike.${like}`,
              `company_name.ilike.${like}`,
              `email.ilike.${like}`,
              `address1.ilike.${like}`,
              `city.ilike.${like}`,
              `state.ilike.${like}`,
              `zip.ilike.${like}`,
              `id.ilike.${like}`,
            ].join(',')
          );
        }
      }

      if (selectedCompanies.length > 0) {
        query = query.in('company_id', selectedCompanies);
      }

      if (agreementFilter.length > 0) {
        query = query.in('agreement_status', agreementFilter);
      }

      if (lastServiceOnly) {
        query = query.not('last_service_date', 'is', null);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      let filtered = (data as Customer[]) || [];
      if (dncOnly) {
        filtered = filtered.filter((c) =>
          (c.customer_do_not_contact || []).some((d) => !d.revoked_at)
        );
      }

      const { deduped, hidden } = dedupeCustomersForList(filtered);
      const pageRows = deduped.slice(0, PAGE_SIZE);

      setCustomers(pageRows);
      setRawRowCount(count ?? filtered.length);
      setHiddenDuplicateCount(hidden);

      // For full 10-digit phone searches, we can either:
      // - show a full SuccessWare-style drilldown (billing account → locations) when exactly one company is selected
      // - or fall back to the lighter billing-account grouping when searching multiple companies.
      if (term) {
        const digitsRaw = term.replace(/\D/g, '');
        let digits = digitsRaw;
        if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
        if (digits.length > 10) digits = digits.slice(-10);

        const isFullPhone = digits.length >= 10;
        const drilldownEnabled = isFullPhone && selectedCompanies.length === 1;

        if (drilldownEnabled) {
          void fetchPhoneHierarchyFor({
            phoneDigits: digits,
            companyId: selectedCompanies[0] || null,
            enabled: true,
          });
          void fetchBillingLookupFor({ phoneDigits: null, companyIds: [], enabled: false });
        } else {
          void fetchPhoneHierarchyFor({ phoneDigits: null, companyId: null, enabled: false });
          void fetchBillingLookupFor({
            phoneDigits: digits,
            companyIds: selectedCompanies,
            enabled: isFullPhone,
          });
        }
      } else {
        void fetchPhoneHierarchyFor({ phoneDigits: null, companyId: null, enabled: false });
        void fetchBillingLookupFor({ phoneDigits: null, companyIds: [], enabled: false });
      }

      // Fetch latest verification status for what we're actually rendering.
      void fetchVerificationsFor(pageRows.map((c) => c.id));

      // Fetch phone intelligence for displayed customers
      void fetchPhoneIntelFor(pageRows.map((c) => c.phone_number));
    } catch (err) {
      console.error('Failed to load customers', err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  const phoneLikeMeta = useMemo(() => {
    const term = debouncedSearch.trim();
    if (!term) return { isPhoneLike: false, isFullPhone: false, digits: '' };

    const digitsRaw = term.replace(/\D/g, '');
    let digits = digitsRaw;
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length > 10) digits = digits.slice(-10);

    return {
      isPhoneLike: digits.length >= 7,
      isFullPhone: digits.length >= 10,
      digits,
    };
  }, [debouncedSearch]);

  const isPhoneDrilldownMode = phoneLikeMeta.isFullPhone && selectedCompanies.length === 1;

  const groupedCustomers = useMemo(() => {
    if (!phoneLikeMeta.isFullPhone) return null;
    if (isPhoneDrilldownMode) return null;
    if (!billingLookupByKey || Object.keys(billingLookupByKey).length === 0) return null;

    const groups = new Map<
      string,
      {
        key: string;
        label: string;
        rows: Customer[];
      }
    >();

    for (const c of customers) {
      const canonicalLocationId = canonicalizeCustomerId(c.id) || c.id;
      const lookupKey = `${c.company_id}|${canonicalLocationId}`;
      const lookup = billingLookupByKey[lookupKey] || null;

      const billingCustomerId = lookup?.billing_customer_id;
      const groupKey = billingCustomerId
        ? `${c.company_id}|billing:${billingCustomerId}`
        : `${c.company_id}|billing:unresolved`;

      const companyLabel = COMPANY_MAP[c.company_id] || c.company_id;
      const label = billingCustomerId
        ? `${companyLabel} • ${lookup?.label ? `${lookup.label} • ` : ''}Billing Account ${billingCustomerId}`
        : `${companyLabel} • Billing Account Unresolved`;

      const existing = groups.get(groupKey);
      if (!existing) {
        groups.set(groupKey, { key: groupKey, label, rows: [c] });
      } else {
        existing.rows.push(c);
      }
    }

    // Deterministic ordering: unresolved billing accounts last, otherwise sort by label.
    return Array.from(groups.values()).sort((a, b) => {
      const aUnresolved = a.key.endsWith('billing:unresolved');
      const bUnresolved = b.key.endsWith('billing:unresolved');
      if (aUnresolved !== bUnresolved) return aUnresolved ? 1 : -1;
      return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [customers, phoneLikeMeta.isFullPhone, billingLookupByKey, isPhoneDrilldownMode]);

  const renderPhoneDrilldown = () => {
    if (!isPhoneDrilldownMode) return null;

    const companyId = selectedCompanies[0];
    const companyLabel = COMPANY_MAP[companyId] || companyId;
    const phoneDigits = phoneLikeMeta.digits;

    return (
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">Billing Account Drilldown</div>
            <div className="text-xs text-gray-500">
              {companyLabel} • {phoneDigits}
            </div>
          </div>

          {phoneHierarchyLoading && <div className="text-xs text-gray-500">Loading…</div>}
        </div>

        {phoneHierarchyLoading ? (
          <div className="p-4">
            <LoadingSkeleton type="list" count={3} />
          </div>
        ) : phoneHierarchyError ? (
          <div className="p-4 text-sm text-red-600">{phoneHierarchyError}</div>
        ) : phoneHierarchyBillingAccounts.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No billing accounts found for this phone.</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {phoneHierarchyBillingAccounts.map((b) => {
              const billingKey = b.billing_customer_id || 'unresolved';
              const expanded = Boolean(expandedBillingAccountKeys[billingKey]);

              return (
                <div key={billingKey}>
                  <button
                    type="button"
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                    onClick={() =>
                      setExpandedBillingAccountKeys((prev) => ({
                        ...prev,
                        [billingKey]: !prev[billingKey],
                      }))
                    }
                  >
                    <div className="flex items-center gap-2">
                      {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <div className="text-sm font-semibold text-gray-900">
                        Billing Account {b.billing_customer_id || 'Unresolved'}
                      </div>
                      {b.label && <div className="text-xs text-gray-500">{b.label}</div>}
                    </div>
                    <div className="text-xs text-gray-500">{(b.locations || []).length} locations</div>
                  </button>

                  {expanded && (
                    <div className="bg-gray-50 px-4 py-3 space-y-2">
                      {(b.locations || []).length === 0 ? (
                        <div className="text-sm text-gray-600">No locations found.</div>
                      ) : (
                        (b.locations || []).map((loc) => {
                          const isExpanded = Boolean(expandedLocationIds[loc.location_id]);
                          const drill = locationDrilldownByLocationId[loc.location_id] || null;

                          const addressLine =
                            [loc.address1, loc.city, loc.state, loc.zip_code]
                              .filter(Boolean)
                              .join(', ') || '—';

                          return (
                            <div key={loc.location_id} className="bg-white border rounded-lg">
                              <button
                                type="button"
                                className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 rounded-lg"
                                onClick={() => {
                                  const next = !isExpanded;
                                  setExpandedLocationIds((prev) => ({ ...prev, [loc.location_id]: next }));

                                  if (next && !drill?.loading && !drill?.data && !drill?.error) {
                                    void fetchLocationDrilldownFor({
                                      phoneDigits,
                                      companyId,
                                      locationId: loc.location_id,
                                      page: 1,
                                      size: 10,
                                    });
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  <div className="text-sm font-medium text-gray-900">
                                    Location {loc.legacy_location_id || loc.location_id}
                                  </div>
                                  {loc.has_internal_membership && (
                                    <span className="text-[11px] border px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border-blue-200">
                                      Internal membership ×{loc.internal_memberships_count || 0}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">{addressLine}</div>
                              </button>

                              {isExpanded && (
                                <div className="px-3 py-2 border-t text-sm">
                                  {drill?.loading ? (
                                    <div className="text-gray-600">Loading details…</div>
                                  ) : drill?.error ? (
                                    <div className="text-red-600">{drill.error}</div>
                                  ) : drill?.data ? (
                                    <div className="space-y-3">
                                      <div>
                                        <div className="text-xs font-semibold text-gray-700 mb-1">Agreements</div>
                                        {drill.data.agreements?.length ? (
                                          <ul className="space-y-1">
                                            {drill.data.agreements.map((a) => {
                                              const status =
                                                typeof (a as any)?.detail?.status === 'string'
                                                  ? String((a as any).detail.status)
                                                  : null;
                                              const startDate =
                                                typeof (a as any)?.detail?.start_date === 'string'
                                                  ? String((a as any).detail.start_date).slice(0, 10)
                                                  : null;
                                              const endDate =
                                                typeof (a as any)?.detail?.end_date === 'string'
                                                  ? String((a as any).detail.end_date).slice(0, 10)
                                                  : null;

                                              const meta = [
                                                a.agreement_type_name || null,
                                                status ? `Status: ${status}` : null,
                                                startDate ? `Start: ${startDate}` : null,
                                                endDate ? `End: ${endDate}` : null,
                                              ]
                                                .filter(Boolean)
                                                .join(' • ');

                                              return (
                                                <li
                                                  key={a.agreement_id}
                                                  className="flex flex-col md:flex-row md:items-start md:justify-between gap-1"
                                                >
                                                  <span className="font-medium">
                                                    {a.agreement_number || a.agreement_id}
                                                  </span>
                                                  <span className="text-xs text-gray-600">{meta || '—'}</span>
                                                </li>
                                              );
                                            })}
                                          </ul>
                                        ) : (
                                          <div className="text-sm text-gray-600">No agreements found.</div>
                                        )}
                                      </div>

                                      <div>
                                        <div className="text-xs font-semibold text-gray-700 mb-1">Internal memberships</div>
                                        {drill.data.internal_memberships?.length ? (
                                          <ul className="space-y-1">
                                            {drill.data.internal_memberships.map((m) => {
                                              const startDate = m.start_date ? String(m.start_date).slice(0, 10) : null;
                                              const nextBill = m.next_billing_date
                                                ? String(m.next_billing_date).slice(0, 10)
                                                : null;
                                              const cancelledAt = m.cancelled_at
                                                ? String(m.cancelled_at).slice(0, 10)
                                                : null;

                                              const meta = [
                                                m.status || null,
                                                m.plan_name || null,
                                                startDate ? `Start: ${startDate}` : null,
                                                nextBill ? `Next bill: ${nextBill}` : null,
                                                cancelledAt ? `Cancelled: ${cancelledAt}` : null,
                                              ]
                                                .filter(Boolean)
                                                .join(' • ');

                                              return (
                                                <li
                                                  key={m.id}
                                                  className="flex flex-col md:flex-row md:items-start md:justify-between gap-1"
                                                >
                                                  <span className="font-medium">{m.membership_number}</span>
                                                  <span className="text-xs text-gray-600">{meta || '—'}</span>
                                                </li>
                                              );
                                            })}
                                          </ul>
                                        ) : (
                                          <div className="text-sm text-gray-600">No internal memberships.</div>
                                        )}
                                      </div>

                                      <div>
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                          <div className="text-xs font-semibold text-gray-700">
                                            Jobs ({drill.data.jobs_source}
                                            {typeof drill.data.jobs_pagination?.total_jobs === 'number'
                                              ? ` • ${drill.data.jobs.length}/${drill.data.jobs_pagination.total_jobs}`
                                              : ''}
                                            )
                                          </div>

                                          {drill.data.jobs_pagination?.has_more && (
                                            <Button
                                              size="sm"
                                              variant="secondary"
                                              onClick={() => {
                                                const nextPage = (drill.data.jobs_pagination?.page || 1) + 1;
                                                void fetchLocationDrilldownFor({
                                                  phoneDigits,
                                                  companyId,
                                                  locationId: loc.location_id,
                                                  page: nextPage,
                                                  size: 10,
                                                  append: true,
                                                });
                                              }}
                                              disabled={drill.loading}
                                            >
                                              {drill.loading ? 'Loading…' : 'Load more'}
                                            </Button>
                                          )}
                                        </div>
                                        {drill.data.jobs?.length ? (
                                          <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                              <thead className="bg-gray-50">
                                                <tr>
                                                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                                                    Job
                                                  </th>
                                                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                                                    Status
                                                  </th>
                                                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                                                    Type
                                                  </th>
                                                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                                                    Date
                                                  </th>
                                                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                                                    Total
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-100">
                                                {drill.data.jobs.map((j, idx) => {
                                                  const dateLabel =
                                                    j.completed_date ||
                                                    j.end_date ||
                                                    j.scheduled_for ||
                                                    j.start_date ||
                                                    '';
                                                  const typeLabel =
                                                    [j.job_type, j.job_class].filter(Boolean).join(' • ') || '—';
                                                  const metaLabel = [
                                                    j.department ? `Dept: ${j.department}` : null,
                                                    j.lead_source ? `Lead: ${j.lead_source}` : null,
                                                    j.priority ? `Priority: ${j.priority}` : null,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(' • ');

                                                  return (
                                                    <tr key={j.job_id || j.job_number || String(idx)}>
                                                      <td className="px-2 py-1 text-sm text-gray-800">
                                                        <div className="font-medium">
                                                          {j.job_number || j.job_id || '—'}
                                                        </div>
                                                        {j.description && (
                                                          <div className="text-xs text-gray-600">{j.description}</div>
                                                        )}
                                                      </td>
                                                      <td className="px-2 py-1 text-sm text-gray-700">
                                                        {j.status || '—'}
                                                      </td>
                                                      <td className="px-2 py-1 text-sm text-gray-700">
                                                        <div>{typeLabel}</div>
                                                        {metaLabel && (
                                                          <div className="text-xs text-gray-600">{metaLabel}</div>
                                                        )}
                                                      </td>
                                                      <td className="px-2 py-1 text-sm text-gray-700">
                                                        {formatDateShort(dateLabel)}
                                                      </td>
                                                      <td className="px-2 py-1 text-sm text-gray-700">
                                                        {formatMoney(j.total_invoice_amount)}
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        ) : (
                                          <div className="text-sm text-gray-600">No jobs found.</div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-gray-600">No details loaded.</div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCustomerRow = (c: Customer) => (
    <tr key={c.id} className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="font-semibold text-gray-900">{formatCustomerDisplayName(c)}</div>
        <div className="text-xs text-gray-500">ID: {c.id}</div>
        {renderDncBadge(c)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-800">{COMPANY_MAP[c.company_id] || c.company_id}</td>
      <td className="px-4 py-3 space-y-1 text-sm text-gray-800">
        {c.email && (
          <div className="flex items-center gap-2 text-gray-700">
            <Mail size={14} />
            <span>{c.email}</span>
            {renderVerificationBadge(verificationsByCustomerId?.[c.id]?.email, 'email')}
          </div>
        )}
        {c.phone_number && (
          <div className="flex items-center gap-2 flex-wrap text-gray-700">
            <Phone size={14} />
            <span>{c.phone_number}</span>
            {renderPhoneIntelBadge(c.phone_number)}
            {renderVerificationBadge(verificationsByCustomerId?.[c.id]?.phone, 'phone')}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-800">
        <div>{c.address1}</div>
        <div className="text-gray-600 text-xs">{[c.city, c.state, c.zip].filter(Boolean).join(', ')}</div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-800">
        <div className="font-medium">{c.agreement_status || '—'}</div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-800">
        <div>{c.last_service_date ? new Date(c.last_service_date).toLocaleDateString() : '—'}</div>
        {c.last_service_amount !== null && c.last_service_amount !== undefined && (
          <div className="text-xs text-gray-600">${Number(c.last_service_amount).toFixed(2)}</div>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-800">
        <div className="flex flex-col gap-2">
          <Button size="sm" variant="secondary" onClick={() => openCustomer360Modal(c)}>
            View 360
          </Button>
          <Button size="sm" variant="danger" onClick={() => openDncModal(c)} disabled={dncSubmitting}>
            Add DNC
          </Button>
        </div>
      </td>
    </tr>
  );

  const renderVerificationBadge = (
    record: VerificationSummary | null | undefined,
    channel: 'email' | 'phone'
  ) => {
    const status = (record?.status || '').toLowerCase();
    const loadingLabel = verificationsLoading ? '…' : 'unverified';
    let label = record?.status || loadingLabel;

    let cls = 'bg-gray-100 text-gray-700 border-gray-200';

    if (channel === 'email') {
      if (status === 'valid') {
        cls = 'bg-green-100 text-green-800 border-green-200';
        label = 'Valid';
      } else if (status === 'risky') {
        cls = 'bg-yellow-100 text-yellow-800 border-yellow-200';
        label = 'Risky';
      } else if (status === 'invalid') {
        cls = 'bg-red-100 text-red-800 border-red-200';
        label = 'Invalid';
      } else if (status === 'error') {
        cls = 'bg-red-100 text-red-800 border-red-200';
        label = 'Error';
      } else if (!record) {
        label = loadingLabel;
      }
    }

    if (channel === 'phone') {
      if (status === 'mobile') {
        cls = 'bg-green-100 text-green-800 border-green-200';
        label = 'Cell';
      } else if (status === 'landline') {
        cls = 'bg-blue-100 text-blue-800 border-blue-200';
        label = 'Landline';
      } else if (status === 'nonfixedvoip' || status === 'fixedvoip' || status === 'voip') {
        cls = 'bg-yellow-100 text-yellow-800 border-yellow-200';
        label = 'VoIP';
      } else if (status === 'tollfree') {
        cls = 'bg-purple-100 text-purple-800 border-purple-200';
        label = 'Toll-free';
      } else if (status === 'premium') {
        cls = 'bg-purple-100 text-purple-800 border-purple-200';
        label = 'Premium';
      } else if (status === 'unknown') {
        cls = 'bg-gray-100 text-gray-700 border-gray-200';
        label = 'Unknown';
      } else if (status === 'invalid') {
        cls = 'bg-red-100 text-red-800 border-red-200';
        label = 'Invalid';
      } else if (status === 'error') {
        cls = 'bg-red-100 text-red-800 border-red-200';
        label = 'Error';
      } else if (!record) {
        label = loadingLabel;
      }
    }

    const titleParts = [record?.provider, record?.checked_at].filter(Boolean);

    return (
      <span
        className={`text-[11px] border px-2 py-0.5 rounded-full ${cls}`}
        title={titleParts.join(' • ')}
      >
        {label}
      </span>
    );
  };

  const renderPhoneIntelBadge = (phoneNumber: string | null | undefined) => {
    const normalized = normalizePhoneKey(phoneNumber);
    if (!normalized) return null;

    const intel = phoneIntelByPhone[normalized];
    if (!intel) {
      if (phoneIntelLoading) {
        return (
          <span className="text-[11px] border px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border-gray-200">
            …
          </span>
        );
      }
      return null;
    }

    const badges: React.ReactNode[] = [];

    // Litigator risk badge (most important)
    if (intel.is_litigator_risk === true) {
      badges.push(
        <span
          key="litigator"
          className="text-[11px] border px-2 py-0.5 rounded-full bg-red-100 text-red-800 border-red-200 font-semibold"
          title={`TCPA Litigator Risk • Checked: ${intel.checked_at || 'unknown'}`}
        >
          ⚠️ Litigator
        </span>
      );
    }

    // Line type badge
    if (intel.line_type) {
      const lineType = intel.line_type.toLowerCase();
      let cls = 'bg-gray-100 text-gray-700 border-gray-200';
      let label = intel.line_type;

      if (lineType === 'mobile') {
        cls = 'bg-green-100 text-green-800 border-green-200';
        label = 'Mobile';
      } else if (lineType === 'landline') {
        cls = 'bg-blue-100 text-blue-800 border-blue-200';
        label = 'Landline';
      } else if (lineType.includes('voip')) {
        cls = 'bg-yellow-100 text-yellow-800 border-yellow-200';
        label = 'VoIP';
      } else if (lineType === 'tollfree') {
        cls = 'bg-purple-100 text-purple-800 border-purple-200';
        label = 'Toll-free';
      }

      badges.push(
        <span
          key="linetype"
          className={`text-[11px] border px-2 py-0.5 rounded-full ${cls}`}
          title={`Carrier: ${intel.carrier || 'unknown'} • Prepaid: ${intel.is_prepaid ? 'Yes' : 'No'}`}
        >
          {label}
        </span>
      );
    }

    return badges.length > 0 ? <>{badges}</> : null;
  };

  const dncCount = useMemo(
    () =>
      customers.filter((c) => (c.customer_do_not_contact || []).some((d) => !d.revoked_at))
        .length,
    [customers]
  );

  const totalPages = Math.max(1, Math.ceil((rawRowCount || customers.length) / PAGE_SIZE));

  const toggleCompany = (id: string) => {
    setSelectedCompanies((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
    setPage(1);
  };

  const toggleAgreement = (value: string) => {
    setAgreementFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const renderDncBadge = (c: Customer) => {
    const activeDnc = (c.customer_do_not_contact || []).filter((d) => !d.revoked_at);
    if (activeDnc.length === 0) return null;
    const channels = activeDnc.map((d) => d.channel).join(', ');
    return (
      <div className="flex items-center gap-1 text-xs text-red-700 bg-red-100 px-2 py-1 rounded-full w-fit">
        <ShieldAlert size={14} />
        <span>DNC: {channels}</span>
      </div>
    );
  };

  const openDncModal = (customer: Customer) => {
    setDncTarget(customer);
    setDncError(null);
    setDncReason('');

    if (customer.email) setDncChannel('email');
    else if (customer.phone_number) setDncChannel('phone');
    else setDncChannel('email');

    setIsDncModalOpen(true);
  };

  const closeDncModal = () => {
    if (dncSubmitting) return;
    setIsDncModalOpen(false);
    setDncTarget(null);
    setDncError(null);
    setDncReason('');
  };

  const closeCustomer360Modal = () => {
    if (customer360Syncing) return;
    setIsCustomer360Open(false);
    setCustomer360Target(null);
    setCustomer360Error(null);
    setCustomer360Jobs([]);
    setCustomer360Membership(null);
    setCustomer360MembershipEvents([]);
    setCustomer360InternalMemberships([]);
    setCustomer360Locations([]);
    setCustomer360Agreements([]);
  };

  const fetchCustomer360 = async (customer: Customer) => {
    try {
      setCustomer360Loading(true);
      setCustomer360Error(null);

      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();

      if (sessionErr) throw sessionErr;
      const token = session?.access_token;
      if (!token) {
        throw new Error('Missing auth token. Please sign in again.');
      }

      const resp = await fetch(`/api/admin/customers/${customer.id}/enriched`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let payload: any = null;
      try {
        payload = await resp.json();
      } catch {
        // ignore
      }

      if (!resp.ok) {
        throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
      }

      const jobs = ((payload?.jobs as CustomerJob[]) || []).map((j) => ({
        ...j,
        scheduled_for: (j as any).start_date || j.scheduled_for,
        completed_date: (j as any).end_date || j.completed_date,
      }));

      jobs.sort((a, b) => {
        const aDate = new Date(a.scheduled_for || a.completed_date || a.fetched_at).getTime();
        const bDate = new Date(b.scheduled_for || b.completed_date || b.fetched_at).getTime();
        return bDate - aDate;
      });

      setCustomer360Jobs(jobs);
      setCustomer360Membership((payload?.membership as SuccesswareMembershipStatus) || null);
      setCustomer360MembershipEvents(
        (payload?.membership_events as SuccesswareMembershipEvent[]) || []
      );
      setCustomer360InternalMemberships(
        (payload?.internal_memberships as InternalMembershipSummary[]) || []
      );
      setCustomer360Locations((payload?.locations as EnrichedLocation[]) || []);
      setCustomer360Agreements((payload?.agreements as EnrichedAgreement[]) || []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setCustomer360Error(msg);
    } finally {
      setCustomer360Loading(false);
    }
  };

  const openCustomer360Modal = (customer: Customer) => {
    setCustomer360Target(customer);
    setCustomer360Error(null);
    setIsCustomer360Open(true);

    // Load whatever is already persisted (fast), then auto-refresh from SuccessWare so
    // the 360 view isn't empty on first open.
    void fetchCustomer360(customer);

    void (async () => {
      try {
        setCustomer360Syncing(true);
        setCustomer360Error(null);
        await syncCustomer360For(customer);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setCustomer360Error(msg);
      } finally {
        setCustomer360Syncing(false);
      }
    })();
  };

  const syncCustomer360For = async (customer: Customer) => {
    const {
      data: { session },
      error: sessionErr,
    } = await supabase.auth.getSession();

    if (sessionErr) throw sessionErr;
    const token = session?.access_token;
    if (!token) {
      throw new Error('Missing auth token. Please sign in again.');
    }

    const resp = await fetch(`/api/admin/customers/${customer.id}/sync-360`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    let payload: any = null;
    try {
      payload = await resp.json();
    } catch {
      // ignore
    }

    if (!resp.ok) {
      throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
    }

    await fetchCustomer360(customer);
  };

  const syncCustomer360 = async () => {
    if (!customer360Target) return;

    try {
      setCustomer360Syncing(true);
      setCustomer360Error(null);

      await syncCustomer360For(customer360Target);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setCustomer360Error(msg);
    } finally {
      setCustomer360Syncing(false);
    }
  };

  const submitDnc = async () => {
    if (!dncTarget) return;

    try {
      setDncSubmitting(true);
      setDncError(null);

      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();

      if (sessionErr) throw sessionErr;
      const token = session?.access_token;
      if (!token) {
        throw new Error('Missing auth token. Please sign in again.');
      }

      const resp = await fetch('/api/dnc/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_id: dncTarget.id,
          channel: dncChannel,
          reason: dncReason || null,
          source: 'portal',
          added_by: session?.user?.email || 'portal',
        }),
      });

      let payload: any = null;
      try {
        payload = await resp.json();
      } catch {
        // ignore
      }

      if (!resp.ok) {
        throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
      }

      setIsDncModalOpen(false);
      setDncTarget(null);
      setDncReason('');
      await fetchCustomers();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setDncError(msg);
    } finally {
      setDncSubmitting(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600 mt-2">
            View SuccessWare customers, filter by company/status, and see DNC flags.
          </p>
        </div>
        <div className="text-right space-y-1">
          <p className="text-sm text-gray-500">Showing (deduped): {customers.length}</p>
          {hiddenDuplicateCount > 0 && (
            <p className="text-xs text-gray-400">Hidden duplicates (this page fetch): {hiddenDuplicateCount}</p>
          )}
          {rawRowCount > 0 && (
            <p className="text-xs text-gray-400">Raw rows (includes duplicates): {rawRowCount}</p>
          )}
          <p className="text-sm text-red-600">DNC flagged: {dncCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search name, email, phone, address, or ID"
              className="input pl-9 w-full"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={dncOnly}
                onChange={(e) => {
                  setDncOnly(e.target.checked);
                  setPage(1);
                }}
              />
              DNC only
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={lastServiceOnly}
                onChange={(e) => {
                  setLastServiceOnly(e.target.checked);
                  setPage(1);
                }}
              />
              Has Last Service
            </label>
          </div>
        </div>

        {phoneLikeMeta.isPhoneLike && !phoneLikeMeta.isFullPhone && (
          <div className="text-xs text-gray-500">
            Tip: enter a full 10-digit phone number to group results by billing account.
          </div>
        )}

        {phoneLikeMeta.isFullPhone && selectedCompanies.length !== 1 && (
          <div className="text-xs text-gray-500">
            Tip: select exactly one company to use the billing account → locations drilldown.
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-gray-700 mb-2 font-semibold">
              <Filter size={16} /> Companies
            </div>
            <div className="flex flex-wrap gap-2">
              {COMPANY_OPTIONS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => toggleCompany(c.id)}
                  className={`px-3 py-1 rounded-full text-sm border ${
                    selectedCompanies.includes(c.id)
                      ? 'bg-blue-100 text-blue-800 border-blue-200'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-gray-700 mb-2 font-semibold">
              <Filter size={16} /> Agreement Status
            </div>
            <div className="flex flex-wrap gap-2">
              {AGREEMENT_OPTIONS.map((status) => (
                <button
                  key={status}
                  onClick={() => toggleAgreement(status)}
                  className={`px-3 py-1 rounded-full text-sm border ${
                    agreementFilter.includes(status)
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <LoadingSkeleton type="list" count={5} />
      ) : isPhoneDrilldownMode ? (
        renderPhoneDrilldown()
      ) : customers.length === 0 ? (
        <div className="card text-center py-12">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No customers found</h3>
          <p className="text-gray-600">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {phoneLikeMeta.isFullPhone && (
            <div className="px-4 py-2 text-xs text-gray-500 border-b">
              Grouped by billing account{billingLookupLoading ? ' (resolving…)' : ''}
              {billingLookupError ? ` • ${billingLookupError}` : ''}
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Agreement</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Last Service</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {groupedCustomers
                ? groupedCustomers.map((group) => (
                    <React.Fragment key={group.key}>
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-4 py-2 text-xs font-semibold text-gray-700">
                          {group.label}
                        </td>
                      </tr>
                      {group.rows.map((c) => renderCustomerRow(c))}
                    </React.Fragment>
                  ))
                : customers.map((c) => renderCustomerRow(c))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={isCustomer360Open}
        onClose={closeCustomer360Modal}
        title="Customer 360"
        maxWidthClassName="max-w-5xl"
      >
        <div className="space-y-4">
          {customer360Target && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="space-y-1">
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCustomerDisplayName(customer360Target)}
                  </div>
                  <div className="text-xs text-gray-600">
                    {COMPANY_MAP[customer360Target.company_id] || customer360Target.company_id} • ID:{' '}
                    {customer360Target.id}
                  </div>
                  {renderDncBadge(customer360Target)}
                </div>

                <div className="flex items-center justify-end gap-2">
                  <Button variant="secondary" onClick={closeCustomer360Modal} disabled={customer360Syncing}>
                    Close
                  </Button>
                  <Button
                    onClick={syncCustomer360}
                    disabled={customer360Syncing || customer360Loading || !customer360Target}
                  >
                    {customer360Syncing ? 'Refreshing…' : 'Refresh 360'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  {customer360Target.phone_number && (
                    <div className="flex items-center gap-2 flex-wrap text-gray-700">
                      <Phone size={14} />
                      <span>{customer360Target.phone_number}</span>
                      {renderPhoneIntelBadge(customer360Target.phone_number)}
                      {renderVerificationBadge(
                        verificationsByCustomerId?.[customer360Target.id]?.phone,
                        'phone'
                      )}
                    </div>
                  )}
                  {customer360Target.email && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mail size={14} />
                      <span>{customer360Target.email}</span>
                      {renderVerificationBadge(
                        verificationsByCustomerId?.[customer360Target.id]?.email,
                        'email'
                      )}
                    </div>
                  )}
                  {(customer360Target.address1 || customer360Target.city || customer360Target.state ||
                    customer360Target.zip) && (
                    <div className="text-gray-700">
                      {[
                        customer360Target.address1,
                        [customer360Target.city, customer360Target.state, customer360Target.zip]
                          .filter(Boolean)
                          .join(', '),
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </div>
                  )}
                </div>

                <div className="space-y-1 text-gray-700">
                  <div>
                    <span className="text-gray-500">Agreement status:</span>{' '}
                    {customer360Target.agreement_status || '—'}
                  </div>
                  <div>
                    <span className="text-gray-500">Last service:</span>{' '}
                    {customer360Target.last_service_date
                      ? new Date(customer360Target.last_service_date).toLocaleDateString()
                      : '—'}
                    {customer360Target.last_service_amount !== null && customer360Target.last_service_amount !== undefined && (
                      <span className="text-gray-500"> • ${Number(customer360Target.last_service_amount).toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2 text-xs text-gray-600">
                <span className="border px-2 py-1 rounded-full bg-white">
                  Jobs: {customer360Jobs.length}
                </span>
                <span className="border px-2 py-1 rounded-full bg-white">
                  Locations: {customer360Locations.length}
                </span>
                <span className="border px-2 py-1 rounded-full bg-white">
                  Agreements: {customer360Agreements.length}
                </span>
                <span className="border px-2 py-1 rounded-full bg-white">
                  Internal memberships: {customer360InternalMemberships.length}
                </span>
              </div>
            </div>
          )}

          {customer360Error && <div className="text-sm text-red-600">{customer360Error}</div>}

          {customer360Loading ? (
            <div className="py-4">
              <LoadingSkeleton type="card" count={2} />
            </div>
          ) : (
            <>
              {/* SuccessWare membership status */}
              <div className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-gray-900">SuccessWare Comfort Club</div>
                  <span
                    className={`text-xs border px-2 py-1 rounded-full ${
                      customer360Membership?.is_member
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-gray-100 text-gray-700 border-gray-200'
                    }`}
                  >
                    {customer360Membership ? (customer360Membership.is_member ? 'Member' : 'Not a member') : 'Unknown'}
                  </span>
                </div>

                {customer360Membership ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span> {customer360Membership.membership_type || '—'}
                    </div>
                    <div>
                      <span className="text-gray-500">Code:</span> {customer360Membership.membership_code || '—'}
                    </div>
                    <div>
                      <span className="text-gray-500">Member since:</span>{' '}
                      {formatDateShort(customer360Membership.member_since)}
                    </div>
                    <div>
                      <span className="text-gray-500">Last checked:</span>{' '}
                      {formatDateTime(customer360Membership.checked_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">No SuccessWare membership status synced yet.</div>
                )}
              </div>

              {/* Membership events */}
              <div className="border rounded-lg p-4 space-y-2">
                <div className="font-semibold text-gray-900">SuccessWare Membership Timeline (observation-based)</div>
                <div className="text-xs text-gray-500">
                  These events reflect when our system observed membership status changes — not agreement start/end
                  dates.
                </div>
                {customer360MembershipEvents.length === 0 ? (
                  <div className="text-sm text-gray-600">No membership events recorded yet.</div>
                ) : (
                  <ul className="text-sm text-gray-700 space-y-1">
                    {customer360MembershipEvents.map((e) => (
                      <li key={e.id} className="flex items-center justify-between">
                        <span className="font-medium">
                          {e.event_type === 'first_seen'
                            ? 'First seen by our system'
                            : e.event_type === 'became_member'
                              ? 'Became a member'
                              : e.event_type === 'became_non_member'
                                ? 'No longer a member'
                                : e.event_type === 'membership_changed'
                                  ? 'Membership changed'
                                  : e.event_type}
                        </span>
                        <span className="text-xs text-gray-500">
                          {e.detected_at ? new Date(e.detected_at).toLocaleString() : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Job history */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="font-semibold text-gray-900">Job History (by phone)</div>
                {customer360Jobs.length === 0 ? (
                  <div className="text-sm text-gray-600">No jobs synced yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Job</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Class</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Location</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Revenue</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {customer360Jobs.slice(0, 25).map((j) => {
                          const dateLabel = j.completed_date || j.scheduled_for || '';
                          const locationId = (j as any).location_id as string | null | undefined;
                          const serviceAccountId = (j as any).service_account_id as string | null | undefined;
                          const revenue = (j as any).total_invoice_amount as number | null | undefined;
                          return (
                            <tr key={j.job_id}>
                              <td className="px-3 py-2 text-sm text-gray-800">
                                <div className="font-medium">{j.job_number || j.job_id}</div>
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-700">{j.status || '—'}</td>
                              <td className="px-3 py-2 text-sm text-gray-700">{j.job_class || '—'}</td>
                              <td className="px-3 py-2 text-sm text-gray-700">
                                <div>{locationId || '—'}</div>
                                {serviceAccountId && (
                                  <div className="text-[11px] text-gray-500">SA: {serviceAccountId}</div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-700">{formatMoney(revenue ?? null)}</td>
                              <td className="px-3 py-2 text-sm text-gray-700">
                                {formatDateTime(dateLabel)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Locations */}
              <div className="border rounded-lg p-4 space-y-2">
                <div className="font-semibold text-gray-900">Locations</div>
                {customer360Locations.length === 0 ? (
                  <div className="text-sm text-gray-600">No locations synced yet.</div>
                ) : (
                  <ul className="space-y-2 text-sm text-gray-700">
                    {customer360Locations.map((loc) => {
                      const address =
                        loc.full_address ||
                        [loc.street, loc.unit, loc.city, loc.state, loc.zip].filter(Boolean).join(', ');
                      return (
                        <li key={loc.location_id} className="flex flex-col gap-1">
                          <div className="font-medium text-gray-900">Location {loc.location_id}</div>
                          <div className="text-gray-700">{address || '—'}</div>
                          <div className="text-xs text-gray-500 flex gap-4 flex-wrap">
                            <span>Jobs: {loc.total_jobs_at_location ?? 0}</span>
                            <span>
                              Revenue: {formatMoney(loc.total_revenue_at_location ?? 0)}
                            </span>
                            <span>First: {formatDateShort(loc.first_service_at_location)}</span>
                            <span>Last: {formatDateShort(loc.last_service_at_location)}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Agreements */}
              <div className="border rounded-lg p-4 space-y-2">
                <div className="font-semibold text-gray-900">Agreements / Memberships (SuccessWare)</div>
                {customer360Agreements.length === 0 ? (
                  <div className="text-sm text-gray-600">No agreements found in the enriched data.</div>
                ) : (
                  <ul className="space-y-2 text-sm text-gray-700">
                    {customer360Agreements.map((agr) => (
                      <li key={agr.agreement_id} className="flex flex-col gap-1">
                        <div className="font-medium text-gray-900">
                          {agr.agreement_type || 'Agreement'} • {agr.status || '—'}
                        </div>
                        <div className="text-xs text-gray-500 flex gap-4 flex-wrap">
                          <span>ID: {agr.agreement_id}</span>
                          <span>Start: {formatDateShort(agr.start_date)}</span>
                          <span>Renewal: {formatDateShort(agr.renewal_date)}</span>
                          <span>Expires: {formatDateShort(agr.expiration_date)}</span>
                          {agr.monthly_amount !== null && agr.monthly_amount !== undefined && (
                            <span>Monthly: {formatMoney(agr.monthly_amount)}</span>
                          )}
                          {agr.annual_amount !== null && agr.annual_amount !== undefined && (
                            <span>Annual: {formatMoney(agr.annual_amount)}</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Internal memberships */}
              <div className="border rounded-lg p-4 space-y-2">
                <div className="font-semibold text-gray-900">Internal Memberships (secondary system)</div>
                {customer360InternalMemberships.length === 0 ? (
                  <div className="text-sm text-gray-600">
                    No internal memberships linked to this SuccessWare location yet.
                  </div>
                ) : (
                  <ul className="text-sm text-gray-700 space-y-2">
                    {customer360InternalMemberships.map((m) => (
                      <li key={m.id} className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-medium">{m.membership_number}</div>
                          <div className="text-xs text-gray-500">{m.plan_name}</div>
                          <div className="text-[11px] text-gray-500">
                            start: {formatDateShort(m.start_date)}
                            {m.cancelled_at ? ` • cancelled: ${formatDateShort(m.cancelled_at)}` : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">{m.status}</div>
                          <div className="text-xs text-gray-500">
                            next: {formatDateShort(m.next_billing_date)}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isDncModalOpen}
        onClose={closeDncModal}
        title="Add Do Not Contact"
      >
        <div className="space-y-4">
          {dncTarget && (
            <div className="text-sm text-gray-700">
              <div className="font-semibold text-gray-900">
                {dncTarget.first_name || dncTarget.company_name || 'Customer'}
              </div>
              <div className="text-xs text-gray-500">
                {COMPANY_MAP[dncTarget.company_id] || dncTarget.company_id} • ID: {dncTarget.id}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Channel</label>
            <select
              value={dncChannel}
              onChange={(e) => setDncChannel(e.target.value as 'email' | 'sms' | 'phone')}
              className="input w-full"
              disabled={dncSubmitting}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="phone">Phone</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Reason (optional)</label>
            <input
              type="text"
              value={dncReason}
              onChange={(e) => setDncReason(e.target.value)}
              placeholder="Customer requested opt-out"
              className="input w-full"
              disabled={dncSubmitting}
            />
          </div>

          {dncError && <div className="text-sm text-red-600">{dncError}</div>}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeDncModal} disabled={dncSubmitting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={submitDnc} disabled={dncSubmitting || !dncTarget}>
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      <div className="card">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          itemsPerPage={PAGE_SIZE}
          // Intentionally omit totalItems to avoid presenting the raw row-count as "customers".
          onPageChange={setPage}
          onItemsPerPageChange={() => {}}
        />
      </div>
    </div>
  );
}